/**
 * ForgotPasswordForm Component
 *
 * Provides a form for users to request a password reset via email.
 * It includes input validation, loading states, and a link to return to the login options.
 *
 * @component
 * @returns {JSX.Element} The rendered forgot password form
 */

import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, TextField, Stack, CircularProgress } from '@mui/material';
import useForgotPassword from './hooks/useForgotPassword';
import useAuthNavigation from '../useAuthNavigation';
import { Body2, Body3, H3 } from '../../../themes/typography';
import '../../../styles/components/LoginForm.css';

export default function ForgotPasswordForm() {
    const { formik, isLinkSent } = useForgotPassword();
    const { loading } = useSelector((state) => state.auth);
    const { showLoginForm } = useAuthNavigation();

    return (
        <React.Fragment>
            <div className="main-login-email-collect-container">
                <div className="login-welcome-greetings">
                    <H3 color="var(--secondary-color)" fontWeight={700}>
                        Reset Password
                    </H3>
                    <Body2 color="var(--tertiary-color)">
                        Enter your email to reset your password
                    </Body2>
                </div>

                <form className="main-login-form" autoComplete="off" onSubmit={formik.handleSubmit}>
                    <div className="forgot-password-method-container slide-transition slide-active">
                        <div className="main-switchable-login-form">
                            <div className="switchable-login-input-container">
                                <TextField
                                    fullWidth
                                    id="email"
                                    name="email"
                                    label="Email"
                                    value={formik.values.email}
                                    disabled={loading}
                                    onChange={(e) => formik.setFieldValue('email', e.target.value)}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                />
                                {isLinkSent && (
                                    <Stack sx={forgotStackStyle}>
                                        We have sent a password reset link to your email. Please
                                        check your inbox.
                                    </Stack>
                                )}
                            </div>

                            <div className="login-methods-container">
                                <div className="login-methods-action-buttons">
                                    <Button
                                        variant="main"
                                        fullWidth
                                        type="submit"
                                        disabled={loading || isLinkSent}
                                    >
                                        {isLinkSent
                                            ? 'Reset Link Sent Successfully'
                                            : 'Send Reset Link'}
                                        {loading && (
                                            <CircularProgress size={20} sx={circularProgressBar} />
                                        )}
                                    </Button>
                                    <Button disableRipple sx={backToOption} onClick={showLoginForm}>
                                        Back to options
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

                <div className="login-terms-agree-container">
                    <Body3>
                        By signing in you accept Shmooz {''}
                        <span className="terms">
                            <Link>Terms of Service</Link>
                        </span>{' '}
                        and
                        <span className="terms">
                            {' '}
                            {''}
                            <Link>Privacy Policy</Link>
                        </span>
                    </Body3>
                </div>
            </div>
        </React.Fragment>
    );
}

const backToOption = {
    padding: '0',
    fontSize: '0.875rem',
    color: '#ccc',
    '&:hover': {
        textDecoration: 'underline'
    }
};

const forgotStackStyle = {
    color: 'var(--secondary-color)',
    background: 'var(--warning-light-bg-color)',
    padding: '0.875rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    alignSelf: 'stretch',
    textAlign: 'left',
    marginTop: '1rem'
};

const circularProgressBar = {
    color: 'var(--primary-color)',
    marginLeft: '0.75rem'
};
